import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import {
  ArrowRight, CheckCircle2,
  Handshake, ArrowDown, FileText, Building2, Globe,
  RefreshCw, Clock, Shield, Mail, Target, TrendingUp, Puzzle, Hotel
} from 'lucide-react';

/* ─── scroll-reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-el ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function SectionTag({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
      {children}
    </span>
  );
}

/* ─── Partner Card ─── */
function PartnerCard({ name, logo, status, subtext, isLive, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className={`bg-white rounded-2xl border p-6 hover:shadow-lg transition-all group ${isLive ? 'border-green-300 ring-2 ring-green-100' : 'border-slate-200 hover:border-[#2563EB]/30'}`} data-testid={`partner-card-${name.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex flex-col items-center text-center">
          {/* Logo placeholder */}
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform overflow-hidden">
            {logo ? (
              <img src={logo} alt={name} className="w-14 h-14 object-contain" />
            ) : (
              <span className="text-3xl font-bold text-slate-400">{name.charAt(0)}</span>
            )}
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">{name}</h3>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
            isLive 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : status.includes('Launching') 
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            {!isLive && !status.includes('Launching') && <Clock className="w-3 h-3" />}
            {status}
          </span>
          {subtext && (
            <p className={`text-xs mt-2 ${isLive ? 'text-green-600' : 'text-slate-500'}`}>{subtext}</p>
          )}
        </div>
      </div>
    </Reveal>
  );
}

/* ─── Benefit Card ─── */
function BenefitCard({ icon: Icon, title, description, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-[#2563EB]/30 hover:shadow-lg transition-all" data-testid={`benefit-${title.slice(0, 20).toLowerCase().replace(/\s/g, '-')}`}>
        <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-[#2563EB]" />
        </div>
        <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
      </div>
    </Reveal>
  );
}

/* ═══════════════════════ PARTNERS PAGE ═══════════════════════ */
import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import littleHotelierLogo from '../../assets/images/partners/little-hotelier.jpeg';
import guestyLogo from '../../assets/images/partners/guesty.jpeg';

const Partners = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // PMS Partners
  const pmsPartners = [
    { name: 'Beds24', logo: beds24Logo, status: 'Officially Live – 23 October 2026', subtext: 'Now available for Malaysian operators' },
    { name: 'Little Hotelier', logo: littleHotelierLogo, status: 'Integration in progress' },
    { name: 'Guesty', logo: guestyLogo, status: 'Integration in progress' },
    { name: 'Mews', logo: 'https://customer-assets.emergentagent.com/job_af234a36-b3fc-42c2-a986-f11930d0eee9/artifacts/884kvqos_IMG_3496.png', status: 'Launching July 2026', subtext: 'Join waitlist for priority access' },
    { name: 'Hostaway', logo: 'https://customer-assets.emergentagent.com/job_af234a36-b3fc-42c2-a986-f11930d0eee9/artifacts/i683ll0w_IMG_3497.png', status: 'Integration in progress' },
    { name: 'Lodgify', logo: 'https://customer-assets.emergentagent.com/job_af234a36-b3fc-42c2-a986-f11930d0eee9/artifacts/lmye8r4y_IMG_3493.png', status: 'Integration in progress' },
  ];

  // Channel Manager Partners
  const channelManagerPartners = [];

  // Partner Benefits
  const benefits = [
    {
      icon: FileText,
      title: 'Enable LHDN Compliant e-Invoicing',
      description: 'Provide your users with Malaysia LHDN compliant e-Invoice generation directly from their booking transactions.'
    },
    {
      icon: Shield,
      title: 'Automated Tax Compliance',
      description: 'Help hospitality operators in Malaysia stay compliant with automated MyInvois submissions and validations.'
    },
    {
      icon: Puzzle,
      title: 'Seamless API Integration',
      description: 'Easy-to-integrate RESTful APIs that connect your PMS or channel manager with StayMii\'s e-invoice engine.'
    },
    {
      icon: TrendingUp,
      title: 'Expand Malaysian Market',
      description: 'Offer enhanced services for hotels and short-term rental operators targeting the Malaysian hospitality market.'
    },
  ];

  return (
    <>
      <SEO
        title="StayMii Technology Partners | PMS & Channel Manager Partnerships Malaysia"
        description="Partner with StayMii to provide Malaysia LHDN compliant e-Invoicing for hospitality operators. Join our ecosystem of PMS and channel manager integrations."
        keywords="StayMii partners, hospitality technology partners, PMS partnership, channel manager partnership, Malaysia e-invoice partner, MyInvois integration partner"
        canonicalUrl="/partners"
      />

      <style>{`
        .reveal-el { opacity:0; transform:translateY(24px); transition: opacity .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1); }
        .reveal-el.revealed { opacity:1; transform:translateY(0); }
        .flow-arrow { animation: flowPulse 2s ease-in-out infinite; }
        @keyframes flowPulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
      `}</style>

      <div className="min-h-screen bg-white" data-testid="partners-page">

        {/* ═══════ NAV ═══════ */}
        <LandingNavBar />

        {/* ═══════ HERO ═══════ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-testid="partners-hero">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" />
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <SectionTag><Handshake className="h-4 w-4" /> Technology Partners</SectionTag>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                StayMii Technology Partners
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                StayMii collaborates with leading hospitality technology providers including property management systems (PMS), channel managers, and booking platforms. Our technology partnerships allow hospitality operators to automatically generate Malaysia LHDN compliant e-Invoices from booking transactions through seamless integrations.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ═══════ PARTNER ECOSYSTEM - PMS ═══════ */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="pms-partners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Building2 className="h-4 w-4" /> Partner Ecosystem</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">PMS & Channel Managers</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Our PMS partners enable seamless e-invoice generation from booking data.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
              {pmsPartners.map((partner, i) => (
                <PartnerCard key={partner.name} {...partner} delay={i * 60} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ PARTNER ECOSYSTEM - CHANNEL MANAGERS ═══════ */}
        {channelManagerPartners.length > 0 && (
        <section className="py-16 lg:py-24 bg-white" data-testid="channel-manager-partners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Globe className="h-4 w-4" /> Partner Ecosystem</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Channel Managers</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Channel manager partners help sync OTA bookings for automated e-invoicing.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6 max-w-2xl mx-auto">
              {channelManagerPartners.map((partner, i) => (
                <PartnerCard key={partner.name} {...partner} delay={i * 60} />
              ))}
            </div>
          </div>
        </section>
        )}

        {/* ═══════ WHY PARTNER WITH STAYMII ═══════ */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="why-partner-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Target className="h-4 w-4" /> Why Partner</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Why Partner With StayMii</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Unlock new opportunities by joining our technology partner ecosystem.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, i) => (
                <BenefitCard key={benefit.title} {...benefit} delay={i * 80} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ INTEGRATION MODEL ═══════ */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" data-testid="integration-model-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><RefreshCw className="h-4 w-4" /> Integration Model</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">How Partners Integrate</h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  A seamless flow from booking platforms to Malaysia's e-invoice infrastructure.
                </p>
              </div>
            </Reveal>

            {/* Flow Diagram */}
            <Reveal delay={100}>
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 lg:p-12">
                <div className="flex flex-col items-center space-y-6">
                  
                  {/* Step 1: OTA Platforms */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#FF5A5F]/10 flex items-center justify-center">
                          <Globe className="w-6 h-6 text-[#FF5A5F]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">OTA Platforms</h3>
                          <p className="text-sm text-slate-500">Online Travel Agencies</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#FF5A5F]/10 text-[#FF5A5F]">Airbnb</span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#003580]/10 text-[#003580]">Booking.com</span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#5392F9]/10 text-[#5392F9]">Agoda</span>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                  </div>

                  {/* Step 2: PMS / Channel Manager */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">PMS / Channel Manager</h3>
                          <p className="text-sm text-slate-500">Technology Partners</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">Beds24</span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">+ More</span>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                  </div>

                  {/* Step 3: StayMii */}
                  <div className="w-full max-w-md">
                    <div className="bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                          <Hotel className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">StayMii Platform</h3>
                          <p className="text-sm text-white/70">e-Invoice Automation</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                  </div>

                  {/* Step 4: LHDN MyInvois */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-emerald-200">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">LHDN MyInvois System</h3>
                          <p className="text-sm text-slate-500">Malaysia e-Invoice Infrastructure</p>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 ml-auto" />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ BECOME A PARTNER ═══════ */}
        <section className="py-16 lg:py-24 bg-white" data-testid="become-partner-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Handshake className="h-4 w-4" /> Join Us</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Become a Partner</h2>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="bg-slate-50 rounded-3xl border border-slate-200 p-8 lg:p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 flex items-center justify-center mx-auto mb-6">
                  <Handshake className="w-8 h-8 text-[#2563EB]" />
                </div>
                <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Are you a PMS, channel manager, or booking platform looking to expand your services for the Malaysian hospitality market? Partner with StayMii to offer automated LHDN compliant e-Invoicing to your users. Our team will work closely with you to ensure a seamless integration experience.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="mailto:partners@staymii.com" 
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold text-lg transition-all hover:shadow-lg group"
                    data-testid="become-partner-btn"
                  >
                    Become a Technology Partner <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
                <p className="mt-6 text-slate-500">
                  Contact us at <a href="mailto:partners@staymii.com" className="text-[#2563EB] font-medium hover:underline">partners@staymii.com</a>
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ CTA ═══════ */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E40AF]" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">Ready to Partner?</h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                Join the StayMii partner ecosystem and help hospitality operators automate e-invoice compliance in Malaysia.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="mailto:partners@staymii.com" className="group px-8 py-4 bg-white text-[#2563EB] rounded-xl font-semibold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-testid="cta-partner-btn">
                  <Mail className="h-5 w-5" /> partners@staymii.com
                </a>
                <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all border border-white/30 flex items-center justify-center gap-2" data-testid="cta-api-btn">
                  View API Docs <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ FOOTER ═══════ */}
        <LandingFooter />
      </div>
    </>
  );
};

export default Partners;
